/* eslint-disable react/only-export-components -- task-type descriptors + registry are tightly coupled to the canvas. */
import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import { FileUp, Link2, ListChecks } from "lucide-react";
import { TASK_STATUS_LABELS, type TaskStatus } from "@quovy/types";

import { cn } from "../../utils";
import { Badge } from "../../data-display/badge";
import { Button } from "../../inputs/button";
import type {
  MockConflictSource,
  MockLinkedResource,
  MockMissingData,
  MockSourceQuote,
  MockVerifyExtraction,
  TaskTypeKey,
} from "./mock-data";

const statusLabel: Record<TaskStatus, string> = Object.fromEntries(
  Object.entries(TASK_STATUS_LABELS).map(([k, v]) => [k, v.toUpperCase()]),
) as Record<TaskStatus, string>;

export interface TaskInboxCanvasItem {
  id: string;
  title: string;
  description?: string | null;
  status: TaskStatus;
  taskType?: string;
  sourceQuote?: MockSourceQuote;
  extraction?: MockVerifyExtraction;
  conflictSources?: MockConflictSource[];
  missingData?: MockMissingData;
  linkedResource?: MockLinkedResource;
}

export type TaskInboxActionVariant = "primary" | "secondary" | "destructive";

export interface TaskInboxAction {
  id: string;
  label: string;
  shortcut?: string;
  variant?: TaskInboxActionVariant;
  onRun: (item: TaskInboxCanvasItem) => void | Promise<void>;
  isAvailable?: (item: TaskInboxCanvasItem) => boolean;
}

export interface TaskInboxCanvasRenderResult {
  body: ReactNode;
  primaryLabel?: string;
  secondaryLabel?: string;
  tertiaryLabel?: string;
}

export interface TaskTypeDescriptor {
  taskType: string;
  actions: TaskInboxAction[] | ((item: TaskInboxCanvasItem) => TaskInboxAction[]);
  listRenderer?: (item: TaskInboxCanvasItem) => ReactNode;
  canvasRenderer: (item: TaskInboxCanvasItem) => TaskInboxCanvasRenderResult;
}

export interface TaskTypeRegistry {
  byType: Record<string, TaskTypeDescriptor>;
  default: TaskTypeDescriptor;
  inferTaskType?: (item: TaskInboxCanvasItem) => string | undefined;
}

export interface TaskInboxCanvasProps {
  task: TaskInboxCanvasItem;
  index: number;
  total: number;
  resourceLabel?: string;
  descriptor: TaskTypeDescriptor;
  className?: string;
}

function ShortcutBox({ children }: { children: React.ReactNode }) {
  return (
    <span
      aria-hidden="true"
      className="inline-flex size-7 shrink-0 items-center justify-center rounded-md border border-border bg-background font-mono text-[11px] font-semibold text-foreground"
    >
      {children}
    </span>
  );
}

function ActionButton({
  shortcut,
  label,
  onClick,
  disabled,
  variant = "secondary",
}: {
  shortcut?: string;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  variant?: TaskInboxActionVariant;
}) {
  return (
    <Button
      type="button"
      variant={
        variant === "primary" ? "default" : variant === "destructive" ? "destructive" : "outline"
      }
      size="row"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "group h-14 gap-3 rounded-lg border px-4 text-[13px] font-medium",
        "focus-visible:ring-2 focus-visible:ring-ring/50",
        variant === "primary"
          ? "border-foreground/20 bg-foreground text-background hover:bg-foreground/90"
          : variant === "destructive"
            ? "border-destructive/40 bg-background text-destructive hover:border-destructive/60 hover:bg-destructive/5"
            : "border-border bg-background text-foreground hover:border-foreground/30 hover:bg-muted/40 hover:shadow-sm",
      )}
    >
      {shortcut ? (
        <span
          className={cn(
            "inline-flex size-7 shrink-0 items-center justify-center rounded-md border font-mono text-[11px] font-semibold",
            variant === "primary"
              ? "border-background/30 bg-background/10 text-background"
              : "border-border bg-background text-foreground",
          )}
        >
          {shortcut.toUpperCase()}
        </span>
      ) : null}
      <span className="flex-1 truncate">{label}</span>
    </Button>
  );
}

function SourceQuoteCard({ quote }: { quote: MockSourceQuote }) {
  const confidencePct = Math.round(quote.confidence * 100);
  const parts = quote.quote.split(quote.highlight);

  return (
    <div className="mt-6 max-w-[62ch] rounded-lg border border-border bg-muted/30 p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2 text-[12px] text-muted-foreground">
          <span className="truncate font-medium text-foreground">{quote.filename}</span>
          <span className="text-border">·</span>
          <span className="shrink-0 tabular-nums">p. {quote.page}</span>
        </div>
        <Badge variant="outline" className="shrink-0 tabular-nums">
          {confidencePct}% confidence
        </Badge>
      </div>

      <blockquote className="mt-3 border-l-2 border-border pl-3 text-[13px] leading-6 text-foreground">
        {parts.length > 1
          ? parts.map((part, i) => (
              <span key={i}>
                {part}
                {i < parts.length - 1 ? (
                  <mark className="rounded-sm bg-amber-200/60 px-0.5 text-foreground dark:bg-amber-500/25">
                    {quote.highlight}
                  </mark>
                ) : null}
              </span>
            ))
          : quote.quote}
      </blockquote>

      <p className="mt-3 text-[12px] text-muted-foreground">
        <span className="font-medium text-foreground">Extracted:</span> {quote.extracted}
      </p>
    </div>
  );
}

function ExtractionPill({ label, value }: { label: string; value: string }) {
  return (
    <div className="mt-4 max-w-[62ch] rounded-lg border border-border bg-background p-4">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        AI extraction
      </div>
      <div className="mt-2 flex items-center justify-between gap-3">
        <span className="text-[12px] text-muted-foreground">{label}</span>
        <code className="rounded-md border border-border bg-muted/50 px-2.5 py-1 font-mono text-[12px] font-medium text-foreground">
          {value}
        </code>
      </div>
    </div>
  );
}

function ConflictSourceCards({
  sources,
  selectedId,
  onSelect,
}: {
  sources: MockConflictSource[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  return (
    <div className="mt-6 grid max-w-[62ch] gap-2">
      {sources.map((source) => {
        const active = source.id === selectedId;
        const confidencePct = Math.round(source.confidence * 100);
        return (
          <Button
            key={source.id}
            type="button"
            variant="ghost"
            size="row"
            onClick={() => onSelect(source.id)}
            aria-pressed={active}
            className={cn(
              "group items-start gap-4 rounded-lg border p-4 focus-visible:ring-2 focus-visible:ring-ring/50",
              active
                ? "border-foreground/40 bg-muted/60 shadow-sm"
                : "border-border bg-background hover:border-foreground/20 hover:bg-muted/30",
            )}
          >
            <span
              aria-hidden="true"
              className={cn(
                "mt-1 inline-flex size-4 shrink-0 items-center justify-center rounded-full border",
                active ? "border-foreground bg-foreground" : "border-border",
              )}
            >
              {active ? <span className="size-1.5 rounded-full bg-background" /> : null}
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-3">
                <span className="text-[12px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {source.sourceLabel}
                </span>
                <Badge variant="outline" className="shrink-0 tabular-nums">
                  {confidencePct}%
                </Badge>
              </div>
              <div className="mt-1.5 font-mono text-[14px] font-medium text-foreground">
                {source.value}
              </div>
              <div className="mt-1.5 flex items-center gap-2 text-[11px] text-muted-foreground">
                <span className="truncate">{source.filename}</span>
                {source.page != null ? (
                  <>
                    <span className="text-border">·</span>
                    <span className="tabular-nums">p. {source.page}</span>
                  </>
                ) : null}
              </div>
            </div>
          </Button>
        );
      })}
    </div>
  );
}

function MissingDataBlock({ data }: { data: MockMissingData }) {
  return (
    <div className="mt-6 max-w-[62ch]">
      <div className="rounded-lg border border-border bg-muted/20 p-4">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Missing
        </div>
        <div className="mt-1 text-[14px] font-medium text-foreground">{data.fieldLabel}</div>
        {data.expectedFormat ? (
          <div className="mt-2 text-[12px] text-muted-foreground">
            <span className="font-medium text-foreground">Expected:</span> {data.expectedFormat}
          </div>
        ) : null}
      </div>

      <div className="mt-3 flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border bg-background px-6 py-8 text-center">
        <div className="flex size-10 items-center justify-center rounded-full bg-muted">
          <FileUp className="size-5 text-muted-foreground" aria-hidden="true" />
        </div>
        <div className="text-[13px] font-medium text-foreground">
          Drop a file or click to upload
        </div>
        {data.acceptedTypes ? (
          <div className="text-[11px] text-muted-foreground">Accepts {data.acceptedTypes}</div>
        ) : null}
      </div>
    </div>
  );
}

function LinkedResourceCard({ resource }: { resource: MockLinkedResource }) {
  return (
    <div className="mt-6 max-w-[62ch] rounded-lg border border-border bg-muted/20 p-5">
      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        <Link2 className="size-3.5" aria-hidden="true" />
        <span>Linked {resource.type}</span>
      </div>
      <div className="mt-2 text-[15px] font-semibold tracking-tight text-foreground">
        {resource.name}
      </div>
      <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-3 text-[12px]">
        {resource.attributes.map((attr) => (
          <div key={attr.label} className="flex flex-col gap-0.5">
            <dt className="text-muted-foreground">{attr.label}</dt>
            <dd className="font-medium text-foreground">{attr.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function TaskDescription({ description }: { description?: string | null | undefined }) {
  if (!description) return null;
  return (
    <p className="mt-3 max-w-[62ch] text-[13px] leading-6 text-muted-foreground">{description}</p>
  );
}

function ConflictBody({ task }: { task: TaskInboxCanvasItem }) {
  const sources = task.conflictSources ?? [];
  const [selectedConflictId, setSelectedConflictId] = useState<string | null>(() => {
    if (!sources.length) return null;
    const best = [...sources].sort((a, b) => b.confidence - a.confidence)[0];
    return best?.id ?? null;
  });

  return (
    <>
      <TaskDescription description={task.description} />
      <ConflictSourceCards
        sources={sources}
        selectedId={selectedConflictId}
        onSelect={setSelectedConflictId}
      />
    </>
  );
}

// Avoid pulling a util for a 3-line truncate.
function truncate(str: string, max: number): string {
  if (str.length <= max) return str;
  return `${str.slice(0, max - 1).trimEnd()}…`;
}

export function resolveDescriptor(
  registry: TaskTypeRegistry,
  item: TaskInboxCanvasItem,
): TaskTypeDescriptor {
  const inferred =
    item.taskType ?? (registry.inferTaskType ? registry.inferTaskType(item) : undefined);
  if (inferred && registry.byType[inferred]) return registry.byType[inferred]!;
  return registry.default;
}

export function resolveActions(
  descriptor: TaskTypeDescriptor,
  item: TaskInboxCanvasItem,
): TaskInboxAction[] {
  const list =
    typeof descriptor.actions === "function" ? descriptor.actions(item) : descriptor.actions;
  return list.filter((a) => (a.isAvailable ? a.isAvailable(item) : true));
}

export function TaskInboxCanvas({
  task,
  index,
  total,
  resourceLabel,
  descriptor,
  className,
}: TaskInboxCanvasProps) {
  const result = useMemo(() => descriptor.canvasRenderer(task), [descriptor, task]);
  const actions = useMemo(() => resolveActions(descriptor, task), [descriptor, task]);

  const visibleActions = actions.slice(0, 3);

  return (
    <div className={cn("flex h-full min-w-0 flex-1 flex-col", className)}>
      <div className="flex-1 overflow-auto px-10 py-8">
        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          <span className="tabular-nums">
            {index}/{total}
          </span>
          <span className="text-border">·</span>
          <span>{statusLabel[task.status]}</span>
          {resourceLabel ? (
            <>
              <span className="text-border">·</span>
              <span>{resourceLabel}</span>
            </>
          ) : null}
        </div>
        <h2 className="mt-3 text-[22px] font-semibold leading-8 tracking-[-0.01em] text-foreground">
          {task.title}
        </h2>
        {result.body}
      </div>

      <div className="border-t border-border px-10 py-5">
        <div className="flex flex-col gap-2">
          {visibleActions.map((action, i) => {
            const label =
              i === 0 && result.primaryLabel
                ? result.primaryLabel
                : i === 1 && result.secondaryLabel
                  ? result.secondaryLabel
                  : i === 2 && result.tertiaryLabel
                    ? result.tertiaryLabel
                    : action.label;
            return (
              <ActionButton
                key={action.id}
                {...(action.shortcut ? { shortcut: action.shortcut } : {})}
                label={label}
                onClick={() => void action.onRun(task)}
                {...(action.variant ? { variant: action.variant } : {})}
              />
            );
          })}
        </div>
        <p className="mt-3 text-[11px] text-muted-foreground">
          Use J / K to navigate between actions.
        </p>
      </div>
    </div>
  );
}

export function TaskInboxCanvasCompletion() {
  return (
    <div className="flex h-full flex-col items-center justify-center px-8 text-center">
      <div className="flex size-12 items-center justify-center rounded-full bg-muted">
        <ListChecks className="size-6 text-foreground" />
      </div>
      <h2 className="mt-4 text-base font-semibold tracking-tight">All tasks complete</h2>
    </div>
  );
}

export function TaskInboxCanvasEmpty() {
  return (
    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
      Select a task to view details.
    </div>
  );
}

// ---------------------------------------------------------------------------
// Default registry: built-in renderers for the five mock task types.
// Action `onRun` callbacks are placeholders; consumers compose their own
// registry by spreading this and overriding `actions`.
// ---------------------------------------------------------------------------

const noop = () => {};

const fallbackActions: TaskInboxAction[] = [
  { id: "complete", label: "Mark complete", shortcut: "a", variant: "primary", onRun: noop },
  { id: "skip", label: "Skip", shortcut: "b", variant: "secondary", onRun: noop },
  {
    id: "flag",
    label: "Flag for review",
    shortcut: "c",
    variant: "secondary",
    onRun: noop,
    isAvailable: (item) => item.status !== "in_review" && item.status !== "done",
  },
];

export const verifyExtractionDescriptor: TaskTypeDescriptor = {
  taskType: "verify_extraction",
  actions: [
    { id: "complete", label: "Accept", shortcut: "a", variant: "primary", onRun: noop },
    { id: "skip", label: "Edit & verify", shortcut: "b", variant: "secondary", onRun: noop },
    {
      id: "flag",
      label: "Flag for review",
      shortcut: "c",
      variant: "secondary",
      onRun: noop,
      isAvailable: (item) => item.status !== "in_review" && item.status !== "done",
    },
  ],
  canvasRenderer: (task) => {
    const value = task.extraction?.extractedValue ?? "";
    return {
      body: (
        <>
          <TaskDescription description={task.description} />
          {task.sourceQuote ? <SourceQuoteCard quote={task.sourceQuote} /> : null}
          {task.extraction ? (
            <ExtractionPill label={task.extraction.fieldLabel} value={value} />
          ) : null}
        </>
      ),
      primaryLabel: value ? `Accept ${truncate(value, 32)}` : "Accept",
    };
  },
};

export const resolveConflictDescriptor: TaskTypeDescriptor = {
  taskType: "resolve_conflict",
  actions: [
    { id: "complete", label: "Use selected value", shortcut: "a", variant: "primary", onRun: noop },
    {
      id: "skip",
      label: "Enter custom value",
      shortcut: "b",
      variant: "secondary",
      onRun: noop,
    },
    {
      id: "flag",
      label: "Flag for review",
      shortcut: "c",
      variant: "secondary",
      onRun: noop,
      isAvailable: (item) => item.status !== "in_review" && item.status !== "done",
    },
  ],
  canvasRenderer: (task) => ({
    body: <ConflictBody task={task} />,
  }),
};

export const missingDataDescriptor: TaskTypeDescriptor = {
  taskType: "missing_data",
  actions: [
    { id: "complete", label: "Upload document", shortcut: "a", variant: "primary", onRun: noop },
    {
      id: "skip",
      label: "Mark not applicable",
      shortcut: "b",
      variant: "secondary",
      onRun: noop,
    },
    {
      id: "flag",
      label: "Flag for review",
      shortcut: "c",
      variant: "secondary",
      onRun: noop,
      isAvailable: (item) => item.status !== "in_review" && item.status !== "done",
    },
  ],
  canvasRenderer: (task) => ({
    body: (
      <>
        <TaskDescription description={task.description} />
        {task.missingData ? <MissingDataBlock data={task.missingData} /> : null}
      </>
    ),
  }),
};

export const reviewLinkedDescriptor: TaskTypeDescriptor = {
  taskType: "review_linked",
  actions: [
    { id: "complete", label: "Open resource", shortcut: "a", variant: "primary", onRun: noop },
    { id: "skip", label: "Acknowledge", shortcut: "b", variant: "secondary", onRun: noop },
    {
      id: "flag",
      label: "Flag for review",
      shortcut: "c",
      variant: "secondary",
      onRun: noop,
      isAvailable: (item) => item.status !== "in_review" && item.status !== "done",
    },
  ],
  canvasRenderer: (task) => ({
    body: (
      <>
        <TaskDescription description={task.description} />
        {task.linkedResource ? <LinkedResourceCard resource={task.linkedResource} /> : null}
      </>
    ),
  }),
};

export const defaultDescriptor: TaskTypeDescriptor = {
  taskType: "default",
  actions: fallbackActions,
  canvasRenderer: (task) => ({
    body: (
      <>
        <TaskDescription description={task.description} />
        {task.sourceQuote ? <SourceQuoteCard quote={task.sourceQuote} /> : null}
      </>
    ),
  }),
};

export const defaultTaskTypeRegistry: TaskTypeRegistry = {
  byType: {
    verify_extraction: verifyExtractionDescriptor,
    resolve_conflict: resolveConflictDescriptor,
    missing_data: missingDataDescriptor,
    review_linked: reviewLinkedDescriptor,
    default: defaultDescriptor,
  },
  default: defaultDescriptor,
};

// Re-export for consumers that want the shortcut indicator on their own.
export { ShortcutBox as TaskInboxShortcutBox };
export type { TaskTypeKey };
